import { useMutation } from '@tanstack/react-query';
import { updateQuoteV2StatusMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2 } from 'Services/Query/QuotesV2/Types';
import { useQuoteCache } from './use-quote-cache';

interface UpdateQuoteStatusVariables {
  statusId: string;
}

/**
 * Update quote status with optimistic updates
 * Only updates statusId, does not trigger full quote edit mutation
 */
export const useUpdateQuoteStatus = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);

  return useMutation({
    ...updateQuoteV2StatusMutation(),
    mutationFn: ({ statusId }: UpdateQuoteStatusVariables) =>
      updateQuoteV2StatusMutation().mutationFn({ quoteId, statusId }),

    onMutate: async ({ statusId }: UpdateQuoteStatusVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      cache.setQuote((old: QuoteV2 | undefined) => {
        if (!old) return old;
        return { ...old, statusId };
      });

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      const ctx = context as { previousQuote?: QuoteV2 };
      if (ctx?.previousQuote) {
        cache.setQuote(() => ctx.previousQuote);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
